import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { DatePreset, DEFAULT_DATE_PRESETS } from "src/config/filterConfig";

interface DateRangeFilterProps {
  /** Current date range values */
  value: { after?: string; before?: string } | undefined;

  /** Callback when range changes */
  onChange: (range: { after?: string; before?: string } | undefined) => void;

  /** Preset date ranges to show */
  presets?: DatePreset[];

  /** Whether to show custom range inputs */
  showCustomRange?: boolean;

  /** Date format for display */
  dateFormat?: "short" | "medium" | "long";
}

/**
 * Date range filter with presets and custom range
 *
 * Features:
 * - Quick preset options (Today, Last 7 days, etc.)
 * - Custom date range inputs
 * - Radio button style selection
 * - Clear option
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  presets = DEFAULT_DATE_PRESETS,
  showCustomRange = true,
  dateFormat = "short",
}) => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customAfter, setCustomAfter] = useState("");
  const [customBefore, setCustomBefore] = useState("");
  const [showCustomInputs, setShowCustomInputs] = useState(false);

  // Determine which preset is active based on current value
  const activePreset = useMemo(() => {
    if (!value?.after && !value?.before) return null;

    // Check each preset to see if it matches
    for (const preset of presets) {
      const presetRange = preset.getValue();
      const presetAfter = formatDateForInput(presetRange.start);
      const presetBefore = formatDateForInput(presetRange.end);

      // Compare dates (ignoring time)
      if (value.after === presetAfter && value.before === presetBefore) {
        return preset.key;
      }
    }

    return "custom";
  }, [value, presets]);

  // Sync state with value
  useEffect(() => {
    if (activePreset === "custom") {
      setShowCustomInputs(true);
      setCustomAfter(value?.after || "");
      setCustomBefore(value?.before || "");
    } else if (activePreset) {
      setSelectedPreset(activePreset);
      setShowCustomInputs(false);
    } else {
      setSelectedPreset(null);
      setShowCustomInputs(false);
    }
  }, [activePreset, value]);

  const handlePresetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const presetKey = event.target.value;

    if (presetKey === "custom") {
      setSelectedPreset("custom");
      setShowCustomInputs(true);
      // Keep current custom values or clear them
      if (!customAfter && !customBefore) {
        onChange(undefined);
      }
    } else if (presetKey === "clear") {
      setSelectedPreset(null);
      setShowCustomInputs(false);
      setCustomAfter("");
      setCustomBefore("");
      onChange(undefined);
    } else {
      const preset = presets.find((p) => p.key === presetKey);
      if (preset) {
        const range = preset.getValue();
        setSelectedPreset(presetKey);
        setShowCustomInputs(false);
        onChange({
          after: formatDateForInput(range.start),
          before: formatDateForInput(range.end),
        });
      }
    }
  };

  const handleCustomDateChange = (
    type: "after" | "before",
    dateValue: string,
  ) => {
    if (type === "after") {
      setCustomAfter(dateValue);
    } else {
      setCustomBefore(dateValue);
    }

    const newAfter = type === "after" ? dateValue : customAfter;
    const newBefore = type === "before" ? dateValue : customBefore;

    if (!newAfter && !newBefore) {
      onChange(undefined);
    } else {
      onChange({
        after: newAfter || undefined,
        before: newBefore || undefined,
      });
    }
  };

  const handleClearCustom = () => {
    setCustomAfter("");
    setCustomBefore("");
    onChange(undefined);
  };

  const formatDateForDisplay = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      const options: Intl.DateTimeFormatOptions =
        dateFormat === "short"
          ? { month: "short", day: "numeric" }
          : dateFormat === "medium"
            ? { month: "short", day: "numeric", year: "numeric" }
            : { month: "long", day: "numeric", year: "numeric" };

      return date.toLocaleDateString("en-US", options);
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <RadioGroup
        value={activePreset || ""}
        onChange={handlePresetChange}
        sx={{ gap: 0 }}
      >
        {/* Preset options */}
        {presets.map((preset) => (
          <FormControlLabel
            key={preset.key}
            value={preset.key}
            control={
              <Radio
                size="small"
                sx={{
                  p: 0.5,
                  "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  color:
                    activePreset === preset.key
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                  fontWeight: activePreset === preset.key ? 500 : 400,
                }}
              >
                {preset.label}
              </Typography>
            }
            sx={{
              mx: 0,
              py: 0.25,
              px: 0.5,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: alpha(theme.palette.action.hover, 0.5),
              },
              ...(activePreset === preset.key && {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              }),
            }}
          />
        ))}

        {/* Custom range option */}
        {showCustomRange && (
          <FormControlLabel
            value="custom"
            control={
              <Radio
                size="small"
                sx={{
                  p: 0.5,
                  "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  color:
                    activePreset === "custom"
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                  fontWeight: activePreset === "custom" ? 500 : 400,
                }}
              >
                Custom range
              </Typography>
            }
            sx={{
              mx: 0,
              py: 0.25,
              px: 0.5,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: alpha(theme.palette.action.hover, 0.5),
              },
              ...(activePreset === "custom" && {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              }),
            }}
          />
        )}
      </RadioGroup>

      {/* Custom date inputs */}
      {showCustomInputs && (
        <Box
          sx={{
            mt: 1.5,
            ml: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <TextField
            size="small"
            type="date"
            label="From"
            value={customAfter}
            onChange={(e) => handleCustomDateChange("after", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8125rem",
              },
              "& .MuiOutlinedInput-input": {
                py: 0.75,
              },
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={customBefore}
            onChange={(e) => handleCustomDateChange("before", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8125rem",
              },
              "& .MuiOutlinedInput-input": {
                py: 0.75,
              },
            }}
          />
          {(customAfter || customBefore) && (
            <Button
              size="small"
              onClick={handleClearCustom}
              sx={{
                fontSize: "0.75rem",
                py: 0.25,
                alignSelf: "flex-start",
              }}
            >
              Clear dates
            </Button>
          )}
        </Box>
      )}

      {/* Active range display */}
      {value && (value.after || value.before) && !showCustomInputs && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            ml: 3,
            color: theme.palette.primary.main,
            fontSize: "0.75rem",
          }}
        >
          {value.after && value.before
            ? `${formatDateForDisplay(value.after)} – ${formatDateForDisplay(
                value.before,
              )}`
            : value.after
              ? `From ${formatDateForDisplay(value.after)}`
              : `Until ${formatDateForDisplay(value.before!)}`}
        </Typography>
      )}
    </Box>
  );
};

// Helper to format date for input[type="date"]
function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default DateRangeFilter;
